import Fuse from 'fuse.js';
import { debounce } from 'throttle-debounce';

/**
 * Initialises events on search input that affect the label.
 * - Fixes issue with on keypress in dropdown that usually foes a first letter search on items.
 * @param {Node} input - search input.
 * @param {Node} label - search label.
 */
const _initNonFuseEvents = (input, label) => {
    // Stops the built in letter based search for intl-tel-input
    input.addEventListener('keydown', e => {
        e.stopPropagation();
    });

    input.addEventListener('change', e => _searchInputHandler(e, label));
    input.addEventListener('blur', e => _searchInputHandler(e, label));
    input.addEventListener('focus', () => (label.style.display = 'none'));
};
/**
 * Controls display of the search label
 * @param {Event} e
 * @param {Node} searchLabel - label to be shown/hidden
 */
const _searchInputHandler = (e, searchLabel) => {
    if (e.target.value !== '') {
        searchLabel.style.display = 'none';
    } else {
        searchLabel.style.display = 'block';
    }
};

/**
 * Reorders the items in the dropdown based on scores from Fuse (automatically sorted).
 * @param {Node} targetEl - dropdown.
 * @param {Array} results - results from Fuse.
 * @param {Node} originalList - full item list to filter through.
 * @param {Object} settings
 */
const _reorderResults = (targetEl, results, originalList, settings) => {
    let items = Array.from(originalList.children);
    let elements = document.createDocumentFragment();

    results.forEach(result => {
        let {
            item: { code }
        } = result;
        let target = items.filter(item => {
            if (settings.byDataCode) {
                if (typeof settings.byDataCode == 'string') {
                    return item.getAttribute(settings.byDataCode) === code;
                } else {
                    return item.getAttribute('data-code') === code;
                }
            } else {
                return item.id.replace(/iti-item-/, '') === code;
            }
        })[0];
        elements.appendChild(target.cloneNode(true));
    });

    targetEl.innerHTML = null;
    targetEl.appendChild(elements);
};

/**
 * Sets Fuse.js options and creates instance.
 * - Sorts search results based on scores for later use.
 * - Includes scores.
 * - Key to target in searchables.
 * @param {Array|Object} searchables - items to be searched.
 * @return Instance of Fuse.js
 */
const _initFuse = searchables => {
    const options = {
        shouldSort: true,
        includeScore: true,
        keys: ['value']
    };
    return new Fuse(searchables, options);
};

/**
 * Attaches a keyup event listener to the search input to trigger Fuse fuzzy search.
 * - Initialises Fuse.js. `_initFuse`.
 * - Creates original child list for reset.
 * - Reorders the items in dropdown based on result scores (accessible on results). `_reorderResults`
 * - Controls accuracy of search using these scores.
 * @param {Node} searchInput - input to attach the listener too.
 * @param {Node} targetEl - dropdown.
 * @param {Array|Object} searchables - items to be searched.
 * @param {Object} settings
 */
const _initSearch = (searchInput, targetEl, searchables, settings) => {
    const fuse = _initFuse(searchables);
    const listItems = Array.from(targetEl.querySelectorAll('.country'));
    const headers = Array.from(targetEl.querySelectorAll('.header'));
    const preferred = Array.from(targetEl.querySelectorAll('.preferred'));
    const divider = targetEl.querySelector('.divider');

    // For reset
    const originalList = document.createDocumentFragment();

    Array.from(targetEl.children).forEach(item => {
        originalList.appendChild(item.cloneNode(true));
    });

    searchInput.addEventListener(
        'keyup',
        debounce(300, e => {
            let { value } = e.target;
            let results;
            // Resets before next search
            listItems.forEach(item => item.classList.remove('filtered'));
            headers.forEach(header => header.classList.remove('d-none'));
            preferred.forEach(pref => pref.classList.remove('d-none'));
            divider.classList.remove('d-none');
            if (value !== '') {
                results = fuse.search(value);

                // Controls accuracy 0 is perfect match, 1 is mismatch
                results = results.filter(result => result.score <= 0.5);

                headers.forEach(header => header.classList.add('d-none'));
                preferred.forEach(pref => pref.classList.add('d-none'));
                divider.classList.add('d-none');

                results.forEach(({ item }) => {
                    let code = item.code;
                    listItems.forEach(listItem => {
                        let id;
                        if (settings.byDataCode) {
                            if (typeof settings.byDataCode == 'string') {
                                id = listItem.getAttribute(settings.byDataCode);
                            } else {
                                id = listItem.getAttribute('data-code');
                            }
                        } else {
                            id = item.id;
                        }

                        if (
                            id.includes(code) ||
                            listItem.classList.contains('filtered')
                        ) {
                            if (!listItem.classList.contains('filtered')) {
                                listItem.classList.add('filtered');
                            }
                            listItem.style.display = 'flex';
                        } else {
                            listItem.style.display = 'none';
                        }
                    });
                });

                _reorderResults(targetEl, results, originalList, settings);
            } else {
                targetEl.innerHTML = null;
                targetEl.appendChild(originalList.cloneNode(true));
            }
        })
    );
};

/**
 * Initialises and adds the search input, label and controls positioning of the dropdown and search box.
 * @param {Function} gettext - handle translations.
 * @param {Node} container - search wrapper element.
 * @param {Object} options - options object, with keys and classes
 */
export const addSearch = (
    gettext,
    container,
    {
        target,
        searchWrapperClass,
        searchInputClass,
        searchLabelClass,
        searchables,
        byDataCode = false
    }
) => {
    const settings = {};
    const targetEl = container.querySelector(target);
    const searchWrapper = document.createElement('div');
    const searchInput = document.createElement('input');
    const searchLabel = document.createElement('label');
    settings.byDataCode = byDataCode;

    searchWrapper.classList.add(searchWrapperClass);

    searchInput.setAttribute('name', 'search');
    searchInput.classList.add(searchInputClass);

    searchLabel.setAttribute('for', 'search');
    searchLabel.classList.add(searchLabelClass);
    searchLabel.textContent = gettext('Search');

    searchWrapper.appendChild(searchLabel);
    searchWrapper.appendChild(searchInput);

    targetEl.insertAdjacentElement('beforebegin', searchWrapper);

    searchWrapper.style.opacity = 0;
    searchWrapper.style.display = 'block';
    targetEl.style.top = `calc(100% + ${searchWrapper.offsetHeight}px)`;
    searchWrapper.removeAttribute('style');

    searchWrapper.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
    });

    _initNonFuseEvents(searchInput, searchLabel);
    _initSearch(searchInput, targetEl, searchables, settings);
};



// WEBPACK FOOTER //
// ./resources/assets/js/helpers/search.js